import React, {Fragment, useContext, useEffect, useMemo} from 'react';
import {css} from '@emotion/css';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {FormProvider, useForm, useWatch} from 'react-hook-form';

import Modal from 'patient-ping-remedy/packages/modal';
import {BannerTypes} from 'patient-ping-remedy/packages/banner';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Spacer from 'patient-ping-remedy/packages/spacer';
import {sizes} from 'patient-ping-remedy/packages/theme';

import PatientDemographics from '@manual_encounters/encounter_admit/pings/PatientDemographicsHookForm.tsx';
import {addDays, format, parseISO, startOfDay} from "date-fns";
import AdmitHookForm from "@manual_encounters/encounter_admit/pings/AdmitHookForm";
import {CarecoApiContext} from "@/app-context/careco-api-context.tsx";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {getGenderAbbreviation} from "@manual_encounters/constants/Genders";
import {StyledSmallTypography} from "@manual_encounters/encounter_client_search/SearchClientModal.styles.tsx";
import {useAlertStore} from "@/store/alert_store.ts";
import Helpers from "@/helpers/helpers";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {formatSubmitInsurancePlans, getInsurancePlanOptions} from "@manual_encounters/encounter_update_status/pings/utils/utils";
import {GENERICERROR, SUCCESS_ADMIT} from "@manual_encounters/constants/BannerMessages";
import InsurancePlanHookForm from "@manual_encounters/encounter_update_status/pings/components/InsurancePlansHookForm";
import {FeatureToggle, GroupType} from "@/api/dto/dto";
import {SearchParam} from "@/helpers/search_params";
import DisplayHelpers from "@/helpers/display_helpers";
import {useStateConfig} from "@/hooks/useStateConfig.js";
import {GroupTypes} from "@clients/profile_page/summary_tab/timeline_components/ADTCard.constants.js";
import PermissionsHelpers from "@/helpers/permissions_helpers.js";
import {useRosterStore} from "@/store/roster_store.js";
import {useAuthStore} from "@/store/auth_store.js";
import {FormSubmitType} from "@manual_encounters/constants/common.js";
import {log} from "@/helpers/logger.js";
import {FormCodeBinding} from "@/api/dto/forms.js";
import {MixpanelEventType} from "@/api/dto/mixpanel.js";
import {useMixpanel} from "@/hooks/useMixpanel.ts";
import {Feature, FeatureNew, FeaturePermissionLevel} from "@/api/dto/permissions.js";
import {router} from "@/router.tsx";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";

const newPatientBanner = {
  type: BannerTypes.INFORMATIONAL,
  text: 'You have no client records similar to the client you are admitting. Please create a new client record.',
};

export const getDefaultValues = (patient, admittingGroup) => {
  const { currentInsurancePlan, patientInsurancePlans } = getInsurancePlanOptions({
    ...patient, currentPing: {
      manual: true,
    }});

  let dob = patient.dateOfBirth;
  if (dob) {
    dob = DisplayHelpers.parseDateOnly(patient.dateOfBirth);
  }

  return {
    firstName: patient.firstName || '',
    lastName: patient.lastName || '',
    insurancePlans: patientInsurancePlans.length > 0 ? patientInsurancePlans
      : [{ insuranceProvider: currentInsurancePlan || { id: 'NEW' }, insuranceValue: '' }],
    gender: patient.gender,
    dob,
    clientId: patient.clientId,
    zip: patient.address?.zip,
    ssn: patient.ssn,
    admittedFrom: { label: patient.currentPingEncounter?.admittedFromLocation || '', id: '' },
    selectedInsurancePlan: currentInsurancePlan || { id: 'NEW' },
    groupId: admittingGroup?.id,
    date: new Date(),
    setting: '',
    phone: DisplayHelpers.formatPhoneNumber(patient.phone),
    phoneType: patient.phoneType,
    address1: patient.address?.address1,
    address2: patient.address?.address2,
    city: patient.address?.city,
    state: patient.address?.state,
    maritalStatus: patient.extendedDemographics?.maritalStatus,
    ethnicity: patient.extendedDemographics?.ethnicity,
    race: patient.extendedDemographics?.race,
    language: patient.extendedDemographics?.language,
    veteranStatus: patient.extendedDemographics?.veteranStatus,
  };
};

export const getRetiredDate = () => {
  return format(addDays(new Date(), 1), 'yyyy-MM-dd');
};


const CreateEditClientModal = ({ hideModal, patient, admittingGroup, type }) => {
  const isCreating = patient.patientId === 'NEW';
  const defaultValues = useMemo(() => getDefaultValues(patient, admittingGroup), [patient, admittingGroup]);
  const hookFormMethods = useForm({ defaultValues, mode: 'onTouched', reValidateMode: 'onChange' });
  const { formState: { errors } } = hookFormMethods;
  const { addAlert } = useAlertStore();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const stateConfig = useStateConfig();
  const { currentRoster } = useRosterStore();
  const { currentUser } = useAuthStore();
  const {carecoApi, encounterApi} = useContext(CarecoApiContext);
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const {checkBooleanToggle} = useFeatureToggles();
  const isReferralModal = type === 'referral';
  const patientId = searchParams.get(SearchParam.PATIENT_ID);

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);

  const {mutateAsync : updatePatientMutation, status: updatePatientStatus} = useMutation({
    mutationFn:(data) => carecoApi?.updateClientProfile(patient.publicPatientId, admittingGroup.id, data),
    mutationKey: ['updatePatient'],
    onSuccess: () => {
      if (isReferralModal) {
        addAlert({ content: 'Client updated successfully.', type: ToastType.SUCCESS });
      }
    },
    onError: (error) => {
      addAlert({
        content: `Failed to update client. ${GENERICERROR} ${Helpers.traceId(error)}`,
        type: ToastType.ERROR
      });
      log('error', error);
    }
  });

  const {mutateAsync : createPatientMutation, status: createPatientStatus} = useMutation({
    mutationFn:(data) => encounterApi?.createPatient( admittingGroup.id, data),
    mutationKey:['createPatient'],
    onError: (error) => {
      addAlert({
        content: `Failed to create client. ${GENERICERROR} ${Helpers.traceId(error)}`,
        type: ToastType.ERROR
      });
      log('error', error);
    }
  });

  const {mutateAsync : admitPatientMutation, status: admitPatientStatus} = useMutation({
    mutationFn: (data) => encounterApi?.createEncounter(data, data.publicPatientId, admittingGroup.id),
    mutationKey: ['admitPatient'],
    onSuccess: () => {
      addAlert({content: SUCCESS_ADMIT, type: ToastType.SUCCESS});
      void queryClient.invalidateQueries({queryKey: ["clients", admittingGroup.id]});

      carecoApi.postMixpanelEvent({
        eventName: MixpanelEventType.BIH_MANUAL_ADMIT_CREATED,
        ...mixpanelEventProperties,
        ...getCurrentGroupDetails(admittingGroup),
      });
    },
    onError: (error) => {
      addAlert({
        content: `Failed to admit client. ${GENERICERROR} ${Helpers.traceId(error)}`,
        type: ToastType.ERROR
      });
      log('error', error);
    }
  });

  useEffect(() => {
    const errorsArray = Object.values(errors);
    if (errorsArray.length > 0) {
      const errorName = errorsArray[0]?.ref?.name;
      const errorNode = document.querySelector(`#${errorName}`);
      errorNode?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errors]);

  const isSnf = useMemo(() => {
    return admittingGroup?.type?.trim().toUpperCase() === GroupType.SNF;
  }, [admittingGroup]);

  const isDeState = stateConfig?.state?.toUpperCase() === 'DE';

  function fetchSelectedInsurance(insurancePlans, currentPayor) {
    if(Helpers.isDefined(currentPayor)) return currentPayor;
    if(insurancePlans.length > 0) {
      const selectedPlan = insurancePlans.find(plan => plan.selected);
      return Helpers.isDefined(selectedPlan) ? selectedPlan : insurancePlans[0];
    }
  }

  async function submitCreating(patientData, submitType) {
    const { firstName, lastName, clientId, gender, dob, ssn, address1, address2, city,
      state, zip, phone, phoneType, maritalStatus, veteranStatus, race, language, ethnicity, insurancePlans } = patientData;

    const formattedInsurancePlans = formatSubmitInsurancePlans(
      defaultValues.insurancePlans??[],
      insurancePlans??[],
      patientData.selectedInsurancePlan??[],
      isCreating??[],
    );

    const formattedPhoneNumber =  phone?.replace(/\D/g, '').substring(0, 10);

    const formattedData = {
      firstName,
      lastName,
      clientId,
      gender : getGenderAbbreviation(gender),
      dateOfBirth : format(new Date(dob), 'yyyy-MM-dd'),
      address: {
        address1,
        address2,
        city,
        state,
        zip,
      },
      phone: formattedPhoneNumber,
      phoneType,
      extendedDemographics: {
        ethnicity,
        maritalStatus,
        veteranStatus,
        race,
        language,
      },
      insurancePlans: formattedInsurancePlans,
      ssn: Helpers.isEmpty(ssn || "") ? null : ssn,
    };

    if (isCreating) {
      createPatientMutation({
        ...formattedData,
        ...(isReferralModal && admittingGroup?.type === GroupType.CRISIS && { retiredDate: getRetiredDate() })
      }).then(({data}) => {
        if (data.clientId) {
          carecoApi.postMixpanelEvent({
            eventName: MixpanelEventType.BIH_CLIENT_IDENTIFIER_ADDED,
            patientId: data.publicPatientId,
            ...mixpanelEventProperties,
          });
        }

        let selectedInsurancePlan = fetchSelectedInsurance(data.insurancePlans, data.currentPayor);
        const updates = { ...patientData, selectedInsurancePlan};

        if(!isReferralModal) {
          handleAdmit(updates, data.publicPatientId, submitType);
        }
        else {
          hideModal();
          router.navigate(`/referrals`+`?${SearchParam.PATIENT_ID}=${data.publicPatientId}`);
        }
      });
      return;
    }
    const updates = {
      ...formattedData,
      publicPatientId: patient.publicPatientId,
      firstName : patient.firstName,
      lastName : patient.lastName,
    };
    updatePatientMutation(updates).then(({data}) => {
      const patientDataUpdates = {
        ...patientData,
        selectedInsurancePlan: {
          id: data.currentInsurancePlans?.find(plan => plan.selected)?.id,
        }
      };
      if(!isReferralModal) {
        handleAdmit(patientDataUpdates, updates.publicPatientId, submitType);
      } else {
        hideModal();
        router.navigate(`/referrals`+`?${SearchParam.PATIENT_ID}=${patient.publicPatientId}`);
      }
    });
  }

  const handleAdmit = async (update, publicPatientId, submitType) => {
    const admitUpdate = {
      publicPatientId: publicPatientId,
      creatorGroupId: admittingGroup.id,
      admit: {
        date: update.date,
        ...(update.admittedFrom?.value !== 'DE_PROVIDERS' && { admittedFromFacilityId: update.admittedFrom?.value }),
        admittedFromFacilityName: update.admittedFrom?.label,
        setting: update.setting,
        selectedInsurancePlan: {
          id: parseInt(update.selectedInsurancePlan?.id),
        },
      },
    };
    const encounterResponse = await admitPatientMutation(admitUpdate);
    hideModal();
    
    if(submitType === FormSubmitType.WITH_CRF) {
      const eventDisplayName = GroupTypes[admittingGroup.type ?? 'Unknown'].displayName;
      const url = `/clients/profile/forms`+
        `?${SearchParam.PATIENT_ID}=${publicPatientId}` +
        `&${SearchParam.ROSTER_ID}=${admittingGroup.id}` +
        `&${SearchParam.FORM_TYPE}=${FormCodeBinding.DSAMH_CRF_ADMISSION_FORM}` +
        `&${SearchParam.FORM_ENCOUNTER}=${encounterResponse.data.id}` +
        `&${SearchParam.FORM_EVENT_TYPE}=${eventDisplayName}` +
        `&${SearchParam.FORM_EVENT_DATE}=${format(new Date(update.date), 'yyyy-MM-dd')}`;
      navigate(url);
      return;
    }

    const currentPage = window.location.pathname;
    if (/^\/clients\/profile(\/.*)?$/.test(currentPage) && patientId === publicPatientId) {
      window.location.reload();
      return;
    }

    const url = `/clients/profile` +
      `?${SearchParam.PATIENT_ID}=${publicPatientId}` +
      `&${SearchParam.ROSTER_ID}=${admittingGroup.id}`;
    navigate(url);
  };

  const modalControls = {
    headerText: `Admit Client - ${admittingGroup.name}`,
    primaryButton: { action: submitCreating, text: 'Admit Client' },
  };

  const referralModalControls = {
    headerText: `Create Referrals - ${admittingGroup.name}`,
    primaryButton: { action: submitCreating, text: 'Create Referral' },
  };

  const isDisabled = modalControls.primaryButton.disabled ||
    [createPatientStatus, updatePatientStatus, admitPatientStatus].includes('pending');
  const banner = isCreating && newPatientBanner;
  const renderEditCreatePatient = () => {
    return (
      <Fragment>
        <PatientDemographics disabled={!isCreating} isReferralModal={isReferralModal} />
        <InsurancePlanHookForm
          isEditing={!isCreating}
          defaultSelectedPlan={defaultValues.selectedInsurancePlan}
        />
        <Spacer itemHeight={sizes.medium} />

        {!isReferralModal && <AdmitHookForm
          isEditing={true}
          patient={patient}
          isPcc={false}
          isSnf={isSnf}
          isDeState={isDeState}
          groupId={admittingGroup?.id}
        />}
      </Fragment>
    );
  };

  const date = useWatch({ control: hookFormMethods.control, name: 'date' });

  const isAdmitWithCrfAvailable = (
    (isNewPermissionsEnabled ?
      PermissionsHelpers.canForUserFeaturePermissionRosterNew(
        currentUser, currentRoster,
        FeaturePermissionLevel.ACT,
        FeatureNew.FORMS
      ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
        currentUser, currentRoster,
        FeaturePermissionLevel.ACT,
        Feature.FORMS
      ))
    && stateConfig.features.crfForms
    && currentRoster.configuration.contractedProvider
    && startOfDay(parseISO(currentRoster.configuration.crfRequiredDate)) <= startOfDay(date)
  );

  const submitButton = (
    <Button
      key="submit"
      styleType={isAdmitWithCrfAvailable ? StyleType.SECONDARY : StyleType.PRIMARY}
      disabled={isDisabled}
      onClick={hookFormMethods.handleSubmit(modalControls.primaryButton.action)}
    >
      {modalControls.primaryButton.text}
    </Button>
  );

  const submitReferralButton = (
    <Button
      key="submit"
      styleType={StyleType.PRIMARY}
      disabled={isDisabled}
      onClick={hookFormMethods.handleSubmit(referralModalControls.primaryButton.action)}
    >
      {referralModalControls.primaryButton.text}
    </Button>
  );

  const cancelButton = (
    <Button key="cancel" styleType={StyleType.TERTIARY} onClick={hideModal}>
      Cancel
    </Button>
  );

  const admitCrfButton = (
    <Button
      key="admit+crf"
      styleType={StyleType.PRIMARY}
      disabled={isDisabled}
      onClick={hookFormMethods.handleSubmit(
        (formData) => modalControls.primaryButton.action(formData, FormSubmitType.WITH_CRF)
      )}
    >
      Admit + CRF form
    </Button>
  );

  const getModalButtons = () => {
    if (isReferralModal) {
      return [submitReferralButton, cancelButton];
    }
    
    if (isAdmitWithCrfAvailable) {
      return [admitCrfButton, submitButton, cancelButton];
    }
    
    return [submitButton, cancelButton];
  };

  return (
    <Modal
      banner={banner}
      headerText={!isReferralModal ? modalControls.headerText : referralModalControls.headerText}
      buttons={getModalButtons()}
      className={css({ width: '75rem' })}
    >
      <StyledSmallTypography>
        All fields marked with <span>*</span> are required.
      </StyledSmallTypography>
      <FormProvider {...hookFormMethods}>{renderEditCreatePatient()}</FormProvider>
    </Modal>
  );
};

export default CreateEditClientModal;
